// ── snapshots.js — 위험 작업 전 자동 백업/복원 ──────────────────
// dedupe·대량 삭제·계정 탈퇴 등 되돌릴 수 없는 작업 직전에 호출.
// users/{uid}/snapshots/{snapshotId} 컬렉션에 데이터 일부 복사.

import {
  collection, addDoc, getDocs, doc, getDoc, deleteDoc,
  serverTimestamp, query, where, limit, orderBy,
} from 'firebase/firestore';
import { db } from '../db';

// ── 스냅샷 생성 ──
// scopes: ['transactions', 'purchaseLog', ...] — 백업할 컬렉션 목록
// label: 사용자에게 보여줄 작업 설명
// reason: 자동 호출자 (예: 'POS-dedupe', 'OCR-dedupe', 'POS-delete-all')
export async function createSnapshot(uid, { label, reason, scopes = ['transactions', 'purchaseLog'], filter = null }) {
  if (!uid) throw new Error('로그인 필요');

  const data = {};
  let totalDocs = 0;
  for (const scope of scopes) {
    const colRef = collection(db, 'users', uid, scope);
    let q = colRef;
    // filter = { field, op, value } — 예: source='pos-import'만 백업
    if (filter && filter.field && filter.op && filter.value !== undefined) {
      q = query(colRef, where(filter.field, filter.op, filter.value));
    }
    const snap = await getDocs(q);
    data[scope] = snap.docs.map(d => ({ _id: d.id, ...d.data() }));
    totalDocs += data[scope].length;
  }

  // 데이터 너무 크면 잘림 (Firestore 문서 1MB 한도). 안전선 800KB.
  const serialized = JSON.stringify(data);
  const sizeKB = Math.round(serialized.length / 1024);
  const truncated = serialized.length > 800 * 1024;

  const snapData = {
    label: label || '(자동 백업)',
    reason: reason || 'manual',
    scopes,
    filter: filter || null,
    totalDocs,
    sizeKB,
    truncated,
    data: truncated ? null : data,  // 너무 크면 데이터 생략, 카운트만 기록
    createdAt: serverTimestamp(),
    createdAtMs: Date.now(),
  };

  const ref = await addDoc(collection(db, 'users', uid, 'snapshots'), snapData);
  return { id: ref.id, totalDocs, sizeKB, truncated };
}

// ── 스냅샷 목록 (최근 N개) ──
export async function listSnapshots(uid, limitCount = 20) {
  if (!uid) return [];
  const q = query(
    collection(db, 'users', uid, 'snapshots'),
    orderBy('createdAtMs', 'desc'),
    limit(limitCount)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ── 스냅샷 한 개 가져오기 (복원 미리보기용) ──
export async function getSnapshot(uid, snapshotId) {
  if (!uid || !snapshotId) return null;
  const ref = doc(db, 'users', uid, 'snapshots', snapshotId);
  const snap = await getDoc(ref);
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// ── 스냅샷 삭제 (오래된 거 정리용) ──
export async function deleteSnapshot(uid, snapshotId) {
  if (!uid || !snapshotId) return;
  await deleteDoc(doc(db, 'users', uid, 'snapshots', snapshotId));
}

// ── 오래된 스냅샷 자동 정리 (보관 일수 지난 것 삭제) ──
export async function cleanupOldSnapshots(uid, maxAgeDays = 30) {
  if (!uid) return 0;
  const cutoff = Date.now() - (maxAgeDays * 24 * 60 * 60 * 1000);
  const q = query(
    collection(db, 'users', uid, 'snapshots'),
    where('createdAtMs', '<', cutoff)
  );
  const snap = await getDocs(q);
  let deleted = 0;
  for (const d of snap.docs) {
    try { await deleteDoc(d.ref); deleted++; }
    catch (e) { console.warn('snapshot cleanup failed:', e.message); }
  }
  return deleted;
}
